import { createContext, ReactNode, useCallback, useContext, useRef, useState } from "react";

interface AudioContextType {
    currentTime: number;
    setCurrentTime: (time: number) => void;
    onTimeUpdate?: (time: number) => void;
    setOnTimeUpdate: (callback: (time: number) => void) => void;
}

const AudioContext = createContext<AudioContextType | undefined>(undefined);

export function AudioProvider({ children }: { children: ReactNode }) {
    const [currentTime, setCurrentTimeState] = useState(0);
    const onTimeUpdateRef = useRef<((time: number) => void) | undefined>(undefined);

    const setCurrentTime = useCallback(
        (time: number) => {
            // Éviter les mises à jour inutiles
            if (Math.abs(time - currentTime) > 0.1) {
                setCurrentTimeState(time);
                if (onTimeUpdateRef.current) {
                    onTimeUpdateRef.current(time);
                }
            }
        },
        [currentTime]
    );

    const setOnTimeUpdate = useCallback((callback: (time: number) => void) => {
        onTimeUpdateRef.current = callback;
    }, []);

    return (
        <AudioContext.Provider
            value={{
                currentTime,
                setCurrentTime,
                onTimeUpdate: onTimeUpdateRef.current,
                setOnTimeUpdate
            }}
        >
            {children}
        </AudioContext.Provider>
    );
}

export function useAudio() {
    const context = useContext(AudioContext);
    if (context === undefined) {
        throw new Error("useAudio must be used within an AudioProvider");
    }
    return context;
}
